// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Analytics wiring for the Бриф promotion console: best-time heatmaps (§5) and
// preferences / timezone (§6). All paths are `/api/publisher/*` (worker rewrites
// `/api/publisher` → `/api/promotion`), no response envelope, errors are
// `{ error }`.
//
//   • Reads (best-time, getPreferences) use the SOFT fetch so the analytics
//     screens keep demonstrating on auth/network failure (never logs out).
//   • The preferences WRITE (savePreferences) uses the STRICT fetch so real
//     errors surface and a 401 triggers the global logout.

import { publisherCall, softCall, type Loaded } from './client';
import type { BestTimeCell, BestTimeHeatmap, Platform } from './types';

// ── Platform list (matches the 8 PLATFORMS the worker emits heatmaps for) ──

const PLATFORMS: Platform[] = [
  'telegram',
  'twitter',
  'instagram',
  'threads',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
];

// ── Best-time (§5) ────────────────────────────────────────────────

/** Coerce an unknown into a finite number, clamped between min and max. */
function clampNum(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function normalizeCell(raw: unknown): BestTimeCell | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    dow: clampNum(r.dow, 0, 6, 0),
    hour: clampNum(r.hour, 0, 23, 0),
    score: clampNum(r.score, 0, 1, 0),
    samples: clampNum(r.samples, 0, Number.MAX_SAFE_INTEGER, 0),
  };
}

function normalizeCells(raw: unknown): BestTimeCell[] {
  if (!Array.isArray(raw)) return [];
  const out: BestTimeCell[] = [];
  for (const item of raw) {
    const cell = normalizeCell(item);
    if (cell) out.push(cell);
  }
  return out;
}

function normalizeHeatmap(raw: unknown, fallbackPlatform: Platform): BestTimeHeatmap {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const platform = (typeof r.platform === 'string' ? r.platform : fallbackPlatform) as Platform;
  const heatmap: BestTimeHeatmap = {
    platform,
    cells: normalizeCells(r.cells),
  };
  if (typeof r.generated_at === 'string') heatmap.generated_at = r.generated_at;
  if (typeof r.window_days === 'number') heatmap.window_days = r.window_days;
  return heatmap;
}

function emptyHeatmap(platform: Platform): BestTimeHeatmap {
  return { platform, cells: [] };
}

/** Days param: default 30, clamped to 1..365. */
function normalizeDays(days?: number): number {
  if (days == null) return 30;
  return Math.round(clampNum(days, 1, 365, 30));
}

/**
 * GET `/api/publisher/best-time?platform=&days=` (§5).
 *
 * Soft read (analytics screen). With a `platform` the worker returns
 * `{ heatmap }` (a single heatmap); without, `{ heatmaps: Record<Platform, …> }`.
 * Either way this resolves to a `Record<Platform, BestTimeHeatmap>` so callers
 * have one consistent shape — single-platform results are keyed by that
 * platform, every other platform getting an empty heatmap.
 */
export function bestTime(
  platform?: Platform,
  days?: number,
): Promise<Loaded<Record<Platform, BestTimeHeatmap>>> {
  const params = new URLSearchParams();
  if (platform) params.set('platform', platform);
  params.set('days', String(normalizeDays(days)));

  const fallback = {} as Record<Platform, BestTimeHeatmap>;
  for (const p of PLATFORMS) fallback[p] = emptyHeatmap(p);

  return softCall<Record<Platform, BestTimeHeatmap>>(
    `/api/publisher/best-time?${params.toString()}`,
    fallback,
    (json) => {
      const j = (json && typeof json === 'object' ? json : {}) as {
        heatmap?: unknown;
        heatmaps?: unknown;
      };

      const out = {} as Record<Platform, BestTimeHeatmap>;
      for (const p of PLATFORMS) out[p] = emptyHeatmap(p);

      // Single-platform shape: { heatmap }
      if (platform && j.heatmap && typeof j.heatmap === 'object') {
        out[platform] = normalizeHeatmap(j.heatmap, platform);
        return out;
      }

      // All-platforms shape: { heatmaps: Record<Platform, BestTimeHeatmap> }
      if (j.heatmaps && typeof j.heatmaps === 'object') {
        const map = j.heatmaps as Record<string, unknown>;
        for (const p of PLATFORMS) {
          if (map[p]) out[p] = normalizeHeatmap(map[p], p);
        }
        return out;
      }

      return out;
    },
  );
}

// ── Preferences / timezone (§6) ───────────────────────────────────

export interface PromotionPreferences {
  timezone: string;
}

/** Read a string `timezone` from either `{ prefs: { timezone } }` or a top-level
 *  `{ timezone }` — the worker may emit either shape. */
function readTimezone(json: unknown): string {
  if (!json || typeof json !== 'object') return '';
  const j = json as { prefs?: unknown; timezone?: unknown };

  if (typeof j.timezone === 'string') return j.timezone;

  if (j.prefs && typeof j.prefs === 'object') {
    const tz = (j.prefs as { timezone?: unknown }).timezone;
    if (typeof tz === 'string') return tz;
  }

  return '';
}

/**
 * GET `/api/publisher/preferences` (§6). Stores ONLY `timezone`. The worker may
 * return `{ prefs: { timezone } }` OR a bare `{ timezone }` — both are read.
 *
 * Soft read: timezone seeds shared UI state, so an auth/network failure should
 * fall back (empty timezone) rather than log the operator out.
 */
export function getPreferences(): Promise<Loaded<PromotionPreferences>> {
  return softCall<PromotionPreferences>(
    '/api/publisher/preferences',
    { timezone: '' },
    (json) => ({ timezone: readTimezone(json) }),
  );
}

/**
 * POST `/api/publisher/preferences` `{ timezone }` (§6) → `{ ok:true; prefs:{ timezone } }`.
 *
 * Strict write: real errors must surface and a 401 logs out. Returns the
 * persisted timezone (read tolerantly from either response shape, falling back
 * to the value we sent).
 */
export async function savePreferences(timezone: string): Promise<PromotionPreferences> {
  const json = await publisherCall<unknown>('/api/publisher/preferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timezone }),
  });

  const saved = readTimezone(json);
  return { timezone: saved || timezone };
}
